import {
    CustomDataSource,
    defined,
    VerticalOrigin,
    HeightReference,
    HorizontalOrigin,
} from 'cesium';
import CockpitIcon from '@vtx/map-icon/lib/map-icon/CockpitIcon';
import FeatureLayer from '../feature-layer/FeatureLayer';
import { createFeatureCollection, getDataByHttp } from '../feature-layer/createFeatureCollection';

class DemandLayer extends FeatureLayer {
    constructor(opt_options) {
        const options = opt_options || {};
        const {
            url,
            params,
            data,
            key,
            name,
            labelField,
            template,
            autoRefresh,
            method,
            renderMode,
            style,
            show = true,
            legend,
            group,
            map,
        } = options;

        if (group?.length) {
            if (!show) {
                group.map(f => {
                    f.show = false;
                });
            } else {
                group.map(f => {
                    f.show = f.show ?? true;
                });
            }
        }

        super(options);

        super.show = show;

        // 接口地址
        this.url = url;

        // 接口地址请求类型
        this.method = method;

        // 接口查询参数
        this.params = params;

        // 图层标识
        this.key = key;

        // 图层名称
        this.name = name;

        // 渲染
        this.style = style;

        // 标签字段
        this.labelField = labelField;

        // 自动刷新标识
        this.autoRefresh = autoRefresh;

        // 模版内容
        this.template = template;

        // 渲染模式
        this.renderMode = renderMode || 'marker';

        // 图例
        this.legend = legend;

        // 过滤
        this.group = group;

        // 地图
        this.map = map;

        // 是否存在地址
        if (url) {
            // 执行
            this.loadUrlData(url, params, method);
        }

        // 直接加载数据
        if (data) {
            // 执行
            this.loadData(data);
        }
    }

    // 重写父类的属性访问器
    get show() {
        return super.show;
    }

    set show(value) {
        this.setVisible(value);
    }

    // 获取地图
    getMap() {
        return this.map;
    }

    // 根据条件获取图形数据
    getFeatureByFieldValue(field, value) {
        return this.getSource()
            .getFeatures()
            .find(v => v.values_[field] === value);
    }

    // 获取当前图层的所有图形
    getFeatures() {
        return this.entities.values;
    }

    // 设置图层显示隐藏
    setVisible(visible) {
        super.show = visible; // 将属性传递给父类

        const map = this.getMap && this.getMap();
        if (map) {
            map.layers.emit('visible', this, visible);
        }
    }

    // 设置过滤条件
    setGroup(group) {
        const features = this.getFeatures();

        const map = this.getMap && this.getMap();

        if (group?.length && features?.length) {
            features.map(feature => {
                let show = false;
                group.map(v => {
                    if (!v.hasOwnProperty('show') || v.show) {
                        let properties = map.getProperties(feature);
                        if (v.filter(properties)) {
                            show = true;
                        }
                    }
                });

                feature.show = show;
            });

            this.group = group;

            this.setVisibleByGroup();
        }
    }

    // 根据过滤条件组显示图层
    setVisibleByGroup() {
        const group = this.group;
        if (group?.length) {
            let visible = false;

            group.map(v => {
                if (!v.hasOwnProperty('show') || v.show) {
                    visible = true;
                }
            });

            this.setVisible(visible);
        }
    }

    // 设置渲染模式
    setRenderMode(renderMode) {
        this.hideClustering();

        // 展示聚合时设置样式为 marker 并且开启聚合监听
        if (renderMode === 'clustering') {
            this.renderMode = 'marker';
            this.setStyle();
            this.showClustering();
        } else {
            this.renderMode = renderMode;
            this.setStyle();
        }
    }

    // 自动刷新
    refresh() {
        getDataByHttp(this.url, this.params, this.method).then(res => {
            if (res?.features?.length) {
            }
        });
    }

    // 加载数据
    loadData(data) {
        this.entities.removeAll();
        let geoJsonData = createFeatureCollection(Array.isArray(data) ? data : [data]);
        this.render(geoJsonData);
    }

    // 加载接口数据
    loadUrlData(url, params, method) {
        this.entities.removeAll();
        getDataByHttp(url, params, method).then(res => {
            if (res?.features?.length) {
                this.render(res);
            }
        });
    }

    // 加载图形数据
    loadFeatures(features) {
        this.entities.removeAll();
        this.getSource().addFeatures(features);
    }

    // 渲染
    render(geoJsonData) {
        if (geoJsonData) {
            const group = this.group;
            let { features } = geoJsonData;
            features.map(v => {
                let entity = createFeature(v);
                if (entity) {
                    let show = true;
                    let style = this.style;

                    // 针对过滤时的处理
                    if (this.group?.length) {
                        show = false;
                        group.map(f => {
                            if (f.filter(v.properties)) {
                                style = f.style;

                                if (!f.hasOwnProperty('show') || f.show) {
                                    show = true;
                                }
                            }
                        });
                    }
                    setRenderer(entity, style);

                    if (style?.type === 'Marker' && style?.style) {
                        let { image, imageType, imageKey } = style.style;
                        if (image && imageType) {
                            if (imageType === 'gif') {
                                entity.setGifImage(image, imageKey);
                            }
                        }
                    }

                    entity.show = show;
                    entity.type = this.modalType || this.key;
                    entity.key = this.key;
                    entity.properties = v.properties || {};

                    this.entities.add(entity);
                }
            });
        }
    }

    // 设置样式
    setStyle() {
        if (this.renderMode === 'point') {
            this.entities.values.map(entity => {
                setRenderer(entity, {
                    type: 'Marker',
                    style: {
                        type: 'PointIcon',
                        size: 15,
                    },
                });
            });
        } else {
            this.entities.values.map(entity => {
                setRenderer(entity, this.style);
            });
        }
    }

    // 展示聚合
    showClustering() {
        // 是否聚合
        this.customStyle();
        this.clustering.pixelRange = 100;
        this.clustering.minimumClusterSize = 2;
        this.clustering.enabled = true;
    }

    // 隐藏聚合
    hideClustering() {
        this.clustering.enabled = false;
        if (defined(this.removeListener) && this.removeListener) {
            this.removeListener();
            this.removeListener = undefined;
        }
    }

    // 自定义样式
    customStyle() {
        let _this = this;
        if (defined(this.removeListener) && this.removeListener) {
            this.removeListener();
            this.removeListener = undefined;
        } else {
            this.removeListener = this.clustering.clusterEvent.addEventListener(function(
                clusteredEntities,
                cluster,
            ) {
                cluster.label.show = false;
                cluster.billboard.show = true;
                cluster.billboard.id = cluster.label.id;
                cluster.billboard.verticalOrigin = VerticalOrigin.BOTTOM;
                cluster.billboard.heightReference = HeightReference.CLAMP_TO_GROUND;
                cluster.billboard.horizontalOrigin = HorizontalOrigin.CENTER;
                cluster.billboard.disableDepthTestDistance = 10000;

                let text = clusteredEntities.length;
                let color = 'rgb(9,220,107)';
                if (clusteredEntities.length >= 50) {
                    text = '50+';
                    color = 'rgb(220,33,9)';
                } else if (clusteredEntities.length >= 40) {
                    text = '40+';
                    color = 'rgb(232,119,5)';
                } else if (clusteredEntities.length >= 30) {
                    text = '30+';
                    color = 'rgb(232,194,4)';
                } else if (clusteredEntities.length >= 20) {
                    text = '20+';
                    color = 'rgba(20, 232, 4, 1)';
                } else if (clusteredEntities.length >= 10) {
                    text = '10+';
                    color = 'rgb(0,128,255)';
                }
                cluster.billboard.image = new CockpitIcon({
                    canvasIcon: _this.style,
                    color,
                    hideFooter: true,
                    info: [{ text, fontSize: 24 }],
                }).getImgUrl();
            });
        }
    }
}

export default DemandLayer;
